/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import Accordion from "./Accordion";
import PrintLabel from "./PrintLabel";
import PrintRows from "./PrintRows";
import { PrintLabelLayoutProvider } from "./PrintLabelLayoutContext";
import { buildPrintGroups } from "../utils/printGrouping";
import { toast } from "react-toastify";
import { postItems } from "../services/api";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { ReceiveTypes } from "../types/receive";

interface PrintProps {
  settings: PrintSettingsStateTypes;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  data: ReceiveTypes[];
}

function PrintReceive(props: PrintProps) {
  const { settings, handleChange, data } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const reactToPrintFn = useReactToPrint({
    contentRef: contentRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    onPrintError: () => {
      setIsPrinting(false);
    },
  });

  const handlePrintProcess = () => {
    setIsPrinting(true);

    setTimeout(() => {
      if (contentRef.current) {
        reactToPrintFn();
      }
    }, 500);
  };

  const printStyles = {
    fontSize: `${settings?.fontSize}px`,
    paddingTop: `${settings?.marginTop}mm`,
    paddingBottom: `${settings?.marginBottom}mm`,
    paddingLeft: `${settings?.marginLeft}mm`,
    paddingRight: `${settings?.marginRight}mm`,
    height: `${settings?.pageHeight - 0.5}cm`,
    width: `${settings?.pageWidth - 0.5}cm`,
  };

  const pageStyle = `
  @media print {
		@page {
         size: ${settings?.pageWidth}cm ${settings?.pageHeight}cm;
         margin: 0px !important;
        }

		.print-container{
          border: none !important;  
		}
		.parent-container{
		  transform: scale(1) !important;  
		}
	}
`;

  const groups = buildPrintGroups(data, settings, !isPrinting);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const productData = await postItems(data);
      if (productData?.data) {
        toast.success("Data item berhasil disimpan!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        reactToPrintFn();
      } else {
        toast.error("Data not found", { position: "top-right" });
      }
    } catch (err: any) {
      toast.error(err.message || "Data not found", { position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  };

  const previewScale =
    (settings?.printPerLine > 5 && settings?.pageWidth > 18) ||
    settings?.pageWidth > 18
      ? 0.58
      : 1;

  const scaleStyles = () => {
    if (
      (settings?.printPerLine > 5 && settings?.pageWidth > 18) ||
      settings?.pageWidth > 18
    ) {
      return {
        transform: "scale(0.58)",
        transformOrigin: "left top",
      };
    }

    return {
      transform: "scale(1)",
    };
  };

  return (
    <Accordion
      title="Print Preview"
      defaultOpen
      content={
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Display Setting</p>
              {Object.entries({
                showDisplay: "Show Data Display",
                showNameDisplay: "Show Name Display",
                showPriceDisplay: "Show Price Display",
                showCodeDisplay: "Show Code Display",
                showDateDisplay: "Show Date Display",
              }).map(([key, label]) => (
                <>
                  {(key !== "showDisplay" && settings?.showDisplay) ||
                  key === "showDisplay" ? (
                    <div key={key}>
                      <label>
                        <input
                          type="checkbox"
                          name={key}
                          checked={(settings as any)[key]}
                          onChange={handleChange}
                        />
                        <span className="ml-2 font-[500]">{label}</span>
                      </label>
                    </div>
                  ) : null}
                </>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Barcode Attributes</p>
              {Object.entries({
                showName: "Show Name",
                showPrice: "Show Price",
                showCode: "Show Code",
                showDate: "Show Date",
                showBarcode: "Show Barcode",
              }).map(([key, label]) => (
                <div key={key}>
                  <label>
                    <input
                      type="checkbox"
                      name={key}
                      checked={(settings as any)[key]}
                      onChange={handleChange}
                    />
                    <span className="ml-2 font-[500]">{label}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <button
              disabled={isLoading}
              onClick={onSubmit}
              className={`px-4 py-2 rounded ${
                isLoading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-green-500 text-white hover:bg-green-600"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <span className="loader-clock"></span>
                  Loading...
                </div>
              ) : (
                "Submit"
              )}
            </button>
            <button
              disabled={isPrinting}
              onClick={handlePrintProcess}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
            >
              Print
            </button>
          </div>
          <br />
          <div className="w-full overflow-x-auto">
            <div
              style={{
                width: `${(settings?.pageWidth - 0.5) * previewScale}cm`,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <div
                ref={contentRef}
                className="parent-container"
                style={scaleStyles()}
              >
                <style>{pageStyle}</style>
                <PrintLabelLayoutProvider>
                  <PrintRows
                    groups={groups}
                    settings={settings}
                    printStyles={printStyles}
                    pageStyle={pageStyle}
                    renderLabel={(label) => (
                      <PrintLabel
                        data={label.product}
                        settings={settings}
                        kind={label.kind}
                      />
                    )}
                  />
                </PrintLabelLayoutProvider>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export default PrintReceive;
